import { useState } from 'react'

import { useMutation } from '@apollo/client'
import {
  Box,
  BoxList,
  Button,
  IconBadge,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  ContractSettingsCountryDataAddOptionDocument,
  ContractSettingsCountryDataArchiveOptionDocument,
  ContractSettingsCountryDataUnarchiveOptionDocument,
  ContractSettingsMaterializedTemplateFragmentOptionFragment,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import EditFieldModal from './EditFieldModal'
import type { EditableOption } from './types'

type IconType = 'pin' | 'lock-locked'

type Props = {
  name: string
  values?: ContractSettingsMaterializedTemplateFragmentOptionFragment[]
  fragmentId: string
  fieldId: string
  archivable?: boolean
  tag?: {
    icon: IconType
    label: string
    hover: string
  }
}

const CountryField = ({ name, values, fragmentId, fieldId, archivable = true, tag }: Props) => {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [options, setOptions] = useState<EditableOption[]>(values || [])
  const [open, toggle] = useToggle()

  const [addOption] = useMutation(ContractSettingsCountryDataAddOptionDocument)
  const [archiveOption] = useMutation(ContractSettingsCountryDataArchiveOptionDocument)
  const [unarchiveOption] = useMutation(ContractSettingsCountryDataUnarchiveOptionDocument)

  useUnsafeEffect(() => {
    if (values) {
      sortOptions(values)
    }
  }, [values])

  const sortOptions = (val: EditableOption[]) => {
    const sortedOptions = [...val].sort((a, b) => {
      if (a.new && !b.new) return 1
      if (!a.new && b.new) return -1
      if (a.archivedAt && !b.archivedAt) return 1
      if (!a.archivedAt && b.archivedAt) return -1
      if (!a.new && !b.new) return a.label.localeCompare(b.label)
      return 0
    })
    setOptions(sortedOptions)
  }

  const onAddOption = () => {
    sortOptions([...options, { id: '', optionId: '', label: '', new: true }])
  }

  const handleInputChange = (index: number, value: string) => {
    const updatedOptions = options.map((option, i) =>
      i === index ? { ...option, label: value } : option
    )
    sortOptions(updatedOptions)
  }

  const onArchiveOption = (index: number) => {
    const updatedOptions = options.map((option, i) =>
      i === index
        ? {
            ...option,
            archivedAt: option.archivedAt ? null : new Date().toISOString(),
            edited: true,
          }
        : option
    )
    sortOptions(updatedOptions)
  }

  const onDeleteOption = (index: number) => {
    const updatedOptions = options.filter((_option, i) => i !== index)
    sortOptions(updatedOptions)
  }

  const handleOnSubmit = async () => {
    await performAction()
    setOpenModal(false)
  }

  const { performAction, running } = useAsyncAction(
    async () => {
      const newOptions = options.filter((option) => option.new && option.label.trim())
      const editedOptions = options.filter((option) => option.edited && !option.new)

      const addPromises = newOptions.map((option) =>
        addOption({
          variables: {
            fieldId,
            fragmentId,
            optionLabel: option.label.trim(),
          },
        })
      )

      const archiveTogglePromises = editedOptions.map((option) => {
        if (option.archivedAt) {
          return archiveOption({
            variables: { fieldId, fragmentId, optionId: option.optionId },
          })
        }
        return unarchiveOption({
          variables: { fieldId, fragmentId, optionId: option.optionId },
        })
      })

      await Promise.all([...addPromises, ...archiveTogglePromises])
    },
    {
      successFlash: true,
      successFlashTime: 1000,
      loadingMessage: i18n.t('common.saving'),
    }
  )

  return (
    <>
      <BoxList.Item>
        <Box flexDirection="row" alignItems="center" width="full" justifyContent="spaceBetween">
          <Box flexDirection="row" gap="s16">
            <IconBadge size="s24" icon="circle" iconColor="viridian1200" color="viridian200" />
            <Text.Normal size="300" weight="medium" color="grey1000">
              {name}
            </Text.Normal>
          </Box>
          <Box gap="s16" flexDirection="row" alignItems="center" justifyContent="flexEnd">
            {!!tag && (
              <Tooltip.Alt attachment="bottom center" open={open} toggle={toggle} label={tag.hover}>
                <Tag type="grey" size="normal" leftItem="icon" icon={tag.icon} iconColor="grey800">
                  {tag.label}
                </Tag>
              </Tooltip.Alt>
            )}
            <Button type="secondary" iconName="arrow-right" onClick={() => setOpenModal(true)} />
          </Box>
        </Box>
      </BoxList.Item>
      {openModal && (
        <EditFieldModal
          fieldName={name}
          options={options}
          onClose={() => setOpenModal(false)}
          onAddOption={() => onAddOption()}
          handleInputChange={handleInputChange}
          onArchiveOption={onArchiveOption}
          onDeleteOption={onDeleteOption}
          onSubmit={handleOnSubmit}
          submitting={running}
          archivable={archivable}
        />
      )}
    </>
  )
}

export default CountryField
